import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useLocation, useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading, Modal } from "src/components/common";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { ModalSize } from "src/enumerations/ModalSize";
import {
    GroupTrialContractsBreadcrumbs,
    TrialGroupContractsBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialGroupContractActions from "../../../actions/trialGroupContract";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    GroupTrialSubMenu,
    TrialGroupSubMenu,
} from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./TrialGroupContractsPage.scss";

interface TrialGroupContractsPageProps {
    trialGroupContracts: Dtos.TrialGroupContract[];
    loadingTrialGroupContracts: boolean;
    loadTrialGroupContractsSuccess: boolean;
    loadTrialGroupContractsFailure: boolean;
    loadTrialGroupContractsErrorCode: string;
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    deletingTrialGroupContract: boolean;
    deleteTrialGroupContractSuccess: boolean;
    clearTrialGroupContracts: TrialGroupContractActions.ITrialGroupContractClearActionCreator;
    loadTrialGroupContracts: TrialGroupContractActions.ITrialGroupContractLoadByTrialGroupIdActionCreator;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    deleteTrialGroupContract: TrialGroupContractActions.ITrialGroupContractDeleteActionCreator;
}

const TrialGroupContractsPage: FC<TrialGroupContractsPageProps> = ({
    trialGroupContracts,
    loadingTrialGroupContracts,
    loadTrialGroupContractsSuccess,
    loadTrialGroupContractsFailure,
    loadTrialGroupContractsErrorCode,
    trialGroup,
    loadingTrialGroup,
    loadTrialGroupSuccess,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    permissions,
    pageSize,
    deletingTrialGroupContract,
    deleteTrialGroupContractSuccess,
    clearTrialGroupContracts,
    loadTrialGroupContracts,
    clearTrialGroup,
    loadTrialGroup,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    deleteTrialGroupContract
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();

    //
    // -- States --
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [tempTrialGroupContract, setTempTrialGroupContract] = useState<
        Dtos.TrialGroupContract | undefined
    >(undefined);

    //
    // -- Variables --
    const showTrialGroupContracts =
        !loadingTrialGroupContracts &&
        loadTrialGroupContractsSuccess &&
        !loadingTrialGroup &&
        loadTrialGroupSuccess;


    // Load Trial group & contracts
    useEffect(() => {
        if (
            trialGroupIdNumber &&
            !trialGroupContracts &&
            !loadingTrialGroupContracts
        ) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupContractsBreadcrumbs
                    : GroupTrialContractsBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber
                )
            );

            loadTrialGroupContracts(trialGroupIdNumber);
            loadTrialGroup(trialGroupIdNumber);
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroupContracts,
        loadingTrialGroupContracts,
        setBreadcrumbs,
        loadTrialGroupContracts,
        loadTrialGroup,
        TrialGroupContractsBreadcrumbs,
        GroupTrialContractsBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupContractsFailure) {
            navigate(`/error/${loadTrialGroupContractsErrorCode}`);
        }
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
    }, [
        loadTrialGroupContractsFailure,
        loadTrialGroupFailure,
        loadTrialGroupContractsErrorCode,
        loadTrialGroupErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialGroupIdNumber && trialGroup?.trial && trialGroup?.group) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupContractsBreadcrumbs
                    : GroupTrialContractsBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials" ? TrialGroupSubMenu : GroupTrialSubMenu;

            setDrawerItems(
                drawerItemSetter(
                    trialGroup?.groupId,
                    trialGroup?.trialId,
                    trialGroupIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroup.trial?.displayName,
                    trialGroup.group?.name
                )
            );
        }
    }, [
        location,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroup,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the trial group & contracts on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroup();
            clearTrialGroupContracts();
        };
    }, [clearTrialGroup, clearTrialGroupContracts]);

    // Reload trial group & contracts after delete
    useEffect(() => {
        if (!deletingTrialGroupContract && deleteTrialGroupContractSuccess) {
            loadTrialGroup(trialGroupIdNumber);
            loadTrialGroupContracts(trialGroupIdNumber);
        }
    }, [
        deletingTrialGroupContract,
        deleteTrialGroupContractSuccess,
        loadTrialGroup,
        loadTrialGroupContracts,
        trialGroupIdNumber,
    ]);

    const generateDeleteButton = (
        trialGroupContract: Dtos.TrialGroupContract
    ) => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialGroupContractUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`contractDeleteButton-${trialGroupContract.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setTempTrialGroupContract(trialGroupContract);
                    setOpenModal(true);
                }}
            >
                <FontAwesomeIcon
                    icon={FontAwesomeIcons.Duotone.TRASH}
                    fixedWidth
                />
            </button>
        );
    };

    const createTable = useMemo(() => {
        let tableData = trialGroupContracts;

        if (!tableData || tableData?.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no trial group contracts to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = tableData.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={tableData}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : tableData.length
                    }
                    pageSize={showPagination ? pageSize : tableData.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no trial group contracts to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const baseRoute =
                                    location?.pathname?.split("/")[1];

                                const url =
                                    baseRoute === "trials"
                                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/contracts/${rowInfo.original.id}/details`
                                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/contracts/${rowInfo.original.id}/details`;

                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.INSTITUTION}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "dateSentToSite",
                            Header: "Date Sent To Site",
                            accessor: "dateSentToSite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "dateConfirmedBySite",
                            Header: "Date Sent To Site",
                            accessor: "dateConfirmedBySite",
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "deleteContract",
                            Header: " ",
                            maxWidth: 75,
                            Cell: (props) => (
                                <div>
                                    {generateDeleteButton(props.original)}
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialGroup,
        trialGroupContracts,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        location,
    ]);

    const generateCreateLink = () => {
        const baseRoute = location?.pathname?.split("/")[1];

        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/contracts/create`
                : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/contracts/create`;

        return url;
    };

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-6 mb-2"}>
                        <h1>Contracts</h1>
                    </div>
                    <div className="col-6" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialGroupContractCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={generateCreateLink()}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialGroupContracts ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialGroupContracts ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showTrialGroupContracts,
        createTable,
        trialIdNumber,
        trialGroupIdNumber,
        generateCreateLink,
    ]);

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.TrialGroupContractUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenModal(false);
                        setTempTrialGroupContract(undefined);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialGroupContract(undefined);
                            }}
                        >
                            <FontAwesomeIcon
                                fixedWidth
                                icon={FontAwesomeIcons.Regular.TIMES}
                            />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the contract "
                            <em>{tempTrialGroupContract?.name}"</em> from trial
                            group{" "}
                            <strong>
                                {trialGroup?.trial?.displayName} -{" "}
                                {trialGroup?.group?.name}
                            </strong>
                            .
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteTrialGroupContract(
                                    tempTrialGroupContract?.id
                                );
                                setOpenModal(false);
                                setTempTrialGroupContract(undefined);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenModal(false);
                                setTempTrialGroupContract(undefined);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        trialGroup,
        tempTrialGroupContract,
        openModal,
        permissions,
        setOpenModal,
        setTempTrialGroupContract,
        trialGroupIdNumber,
        loadTrialGroupContracts,
        deleteTrialGroupContract,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupContractView}
            loading={loadingTrialGroupContracts || loadingTrialGroup}
        >
            {renderContent}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupContractsPageProps> => {
    let trialGroupContracts: Dtos.TrialGroupContract[] | undefined =
        state.trialGroupContract.formData instanceof Array
            ? state.trialGroupContract.formData
            : undefined;
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupContracts: trialGroupContracts!,
        loadingTrialGroupContracts:
            state.trialGroupContract.loadState &&
            state.trialGroupContract.loadState.status == RequestState.Pending,
        loadTrialGroupContractsSuccess:
            state.trialGroupContract.loadState &&
            state.trialGroupContract.loadState.status == RequestState.Success,
        loadTrialGroupContractsFailure:
            state.trialGroupContract.loadState &&
            state.trialGroupContract.loadState.status == RequestState.Failure,
        loadTrialGroupContractsErrorCode:
            state.trialGroupContract.loadState.errorCode,
        trialGroup: trialGroup!,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        deletingTrialGroupContract:
            state.trialGroupContract.deleteState &&
            state.trialGroupContract.deleteState.status == RequestState.Pending,
        deleteTrialGroupContractSuccess:
            state.trialGroupContract.deleteState &&
            state.trialGroupContract.deleteState.status == RequestState.Success,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupContractsPageProps> => {
    return {
        loadTrialGroupContracts: bindActionCreators(
            TrialGroupContractActions.LoadTrialGroupContractByTrialGroupId,
            dispatch
        ),
        clearTrialGroupContracts: bindActionCreators(
            TrialGroupContractActions.Clear,
            dispatch
        ),
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        deleteTrialGroupContract: bindActionCreators(
            TrialGroupContractActions.DeleteTrialGroupContract,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupContractsPage);
